import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Move, ZoomIn, Image as ImageIcon, Undo2, Redo2, FlipHorizontal, FlipVertical, Trash2 } from 'lucide-react';
import { ImageEditorToolbar, ImageTransform } from './ImageEditor';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';

interface ImageEditPortalProps {
  cell: {
    id: string;
    image: string;
    transform?: ImageTransform;
  };
  cellRect: DOMRect;
  onClose: () => void;
  onReplace: () => void;
  onDelete: () => void;
  onFlipH: () => void;
  onFlipV: () => void;
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onSetScale: (scale: number) => void;
  onMoveImage: (offsetX: number, offsetY: number) => void;
}

/**
 * ПОДХОД КОНКУРЕНТА (mycollages.ru):
 * 
 * 1. Изображение (<img>) внутри draggable контейнера
 * 2. Контейнер имеет left/top для позиционирования
 * 3. Внешний wrapper с overflow: hidden обрезает
 * 4. offsetX/offsetY - смещение в пикселях (храним как процент от ячейки для масштабируемости)
 * 
 * Структура:
 * <div class="cell-wrapper" style="overflow: hidden">
 *   <div class="draggable" style="left: Xpx; top: Ypx">
 *     <img style="width: Wpx; height: Hpx" />
 *   </div>
 * </div>
 */

const ImageEditPortal: React.FC<ImageEditPortalProps> = ({
  cell,
  cellRect,
  onClose,
  onReplace,
  onDelete,
  onFlipH,
  onFlipV,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSetScale,
  onMoveImage,
}) => {
  const isMobile = useIsMobile();
  const { t: translations } = useLanguage();
  const dragRef = useRef<HTMLDivElement>(null);
  
  const HINT_STORAGE_KEY = 'collage_image_edit_hint_shown';
  const [showHint, setShowHint] = useState(() => {
    try {
      return localStorage.getItem(HINT_STORAGE_KEY) !== 'true';
    } catch {
      return true;
    }
  });
  
  const t = cell.transform || { flipH: false, flipV: false, offsetX: 0, offsetY: 0, scale: 1 };
  const scale = t.scale || 1;
  const aspectRatio = t.aspectRatio || 1;
  // offsetX/offsetY теперь в процентах от размера ячейки (0 = центр)
  const offsetX = t.offsetX || 0;
  const offsetY = t.offsetY || 0;

  useEffect(() => {
    if (showHint) {
      try {
        localStorage.setItem(HINT_STORAGE_KEY, 'true');
      } catch {}
      const timer = setTimeout(() => setShowHint(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [showHint]);

  const [workBounds, setWorkBounds] = useState<DOMRect | null>(null);
  const [canvasBottom, setCanvasBottom] = useState<number>(0);
  const MOBILE_TOOLBAR_HEIGHT = 56; // Height of editing toolbar
  
  useEffect(() => {
    const header = document.querySelector('header') as HTMLElement;
    const headerHeight = header ? header.getBoundingClientRect().height : 80;
    
    // Find the canvas/template container to limit work area
    const canvas = document.getElementById('collage-canvas');
    const canvasRect = canvas?.getBoundingClientRect();
    
    // Store canvas bottom for toolbar positioning (above download block)
    if (canvasRect) {
      setCanvasBottom(canvasRect.bottom);
    }
    
    let workRect: DOMRect;
    if (isMobile) {
      // On mobile, limit work area to canvas bounds (not full screen)
      const canvasBottomPos = canvasRect ? canvasRect.bottom : window.innerHeight - MOBILE_TOOLBAR_HEIGHT;
      const workHeight = Math.min(
        canvasBottomPos - headerHeight,
        window.innerHeight - headerHeight - MOBILE_TOOLBAR_HEIGHT
      );
      
      workRect = new DOMRect(
        0,
        headerHeight,
        window.innerWidth,
        workHeight
      );
    } else {
      const sidebar = document.querySelector('.w-\\[420px\\]') as HTMLElement || 
                      document.querySelector('[class*="w-80"]') as HTMLElement ||
                      document.querySelector('.flex-shrink-0') as HTMLElement;
      const sidebarWidth = sidebar ? sidebar.getBoundingClientRect().right : 420;
      
      workRect = new DOMRect(
        sidebarWidth,
        headerHeight,
        window.innerWidth - sidebarWidth,
        window.innerHeight - headerHeight
      );
    }
    
    setWorkBounds(workRect);
  }, [isMobile, cellRect]);

  /**
   * Вычисляем размеры изображения для "cover" поведения
   * Возвращает { width, height } изображения в пикселях
   */
  const getImageDimensions = useCallback(() => {
    const cellW = cellRect.width;
    const cellH = cellRect.height;
    const cellRatio = cellW / cellH;
    
    let baseWidth: number;
    let baseHeight: number;
    
    // Cover: изображение покрывает всю ячейку
    if (aspectRatio > cellRatio) {
      // Изображение шире ячейки - подгоняем по высоте
      baseHeight = cellH;
      baseWidth = cellH * aspectRatio;
    } else {
      // Изображение выше ячейки - подгоняем по ширине
      baseWidth = cellW;
      baseHeight = cellW / aspectRatio;
    }
    
    // Применяем масштаб
    return {
      width: baseWidth * scale,
      height: baseHeight * scale,
    };
  }, [cellRect.width, cellRect.height, aspectRatio, scale]);

  /**
   * Вычисляем позицию left/top изображения
   * offsetX/offsetY - смещение от центра в процентах от размера ячейки
   */
  const getImagePosition = useCallback(() => {
    const cellW = cellRect.width;
    const cellH = cellRect.height;
    const dims = getImageDimensions();
    
    // Центрированная позиция (изображение по центру ячейки)
    const centerX = (cellW - dims.width) / 2;
    const centerY = (cellH - dims.height) / 2;
    
    // Смещение в пикселях (offsetX/offsetY в процентах от ячейки)
    const pixelOffsetX = (offsetX / 100) * cellW;
    const pixelOffsetY = (offsetY / 100) * cellH;
    
    return {
      left: centerX + pixelOffsetX,
      top: centerY + pixelOffsetY,
    };
  }, [cellRect.width, cellRect.height, offsetX, offsetY, getImageDimensions]);

  // Drag handling - ref to store cleanup function
  const cleanupDragRef = useRef<(() => void) | null>(null);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (cleanupDragRef.current) {
        cleanupDragRef.current();
        cleanupDragRef.current = null;
      }
    };
  }, []);

  const handleDragStart = useCallback((clientX: number, clientY: number) => {
    // Clean up any existing drag handlers first
    if (cleanupDragRef.current) {
      cleanupDragRef.current();
    }
    
    const startX = clientX;
    const startY = clientY;
    const startOffsetX = offsetX;
    const startOffsetY = offsetY;
    const cellW = cellRect.width;
    const cellH = cellRect.height;
    const dims = getImageDimensions();

    const handleMove = (moveX: number, moveY: number) => {
      // Смещение в пикселях (БЕЗ инверсии при flip - движение должно быть естественным)
      const deltaXPx = moveX - startX;
      const deltaYPx = moveY - startY;
      
      // Конвертируем в проценты от ячейки
      const deltaXPercent = (deltaXPx / cellW) * 100;
      const deltaYPercent = (deltaYPx / cellH) * 100;
      
      let newOffsetX = startOffsetX + deltaXPercent;
      let newOffsetY = startOffsetY + deltaYPercent;
      
      // Ограничения: изображение не должно выходить за края ячейки
      // Максимальное смещение = (размер изображения - размер ячейки) / 2 в процентах
      const maxOffsetX = ((dims.width - cellW) / 2 / cellW) * 100;
      const maxOffsetY = ((dims.height - cellH) / 2 / cellH) * 100;
      
      newOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, newOffsetX));
      newOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, newOffsetY));
      
      onMoveImage(newOffsetX, newOffsetY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault();
      handleMove(e.clientX, e.clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    const cleanup = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', cleanup);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', cleanup);
      cleanupDragRef.current = null;
    };
    
    cleanupDragRef.current = cleanup;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', cleanup);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', cleanup);
  }, [offsetX, offsetY, cellRect.width, cellRect.height, t.flipH, t.flipV, getImageDimensions, onMoveImage]);

  // Wheel zoom
  const scaleRef = useRef(scale);
  const onSetScaleRef = useRef(onSetScale);
  
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  
  useEffect(() => {
    onSetScaleRef.current = onSetScale;
  }, [onSetScale]);

  useEffect(() => {
    const container = dragRef.current;
    if (!container) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newScale = Math.min(3, Math.max(1, scaleRef.current + delta));
      onSetScaleRef.current(newScale);
    };

    container.addEventListener('wheel', handleWheel, { passive: false });
    return () => container.removeEventListener('wheel', handleWheel);
  }, [workBounds]);

  // Pinch-to-zoom
  useEffect(() => {
    const container = dragRef.current;
    if (!container) return;

    let initialDistance = 0;
    let initialScale = 1;

    const getDistance = (touches: TouchList) => {
      if (touches.length < 2) return 0;
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        initialDistance = getDistance(e.touches);
        initialScale = scaleRef.current;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && initialDistance > 0) {
        e.preventDefault();
        const currentDistance = getDistance(e.touches);
        const delta = (currentDistance - initialDistance) / 200;
        onSetScaleRef.current(Math.min(3, Math.max(1, initialScale + delta)));
      }
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
    };
  }, [workBounds]);

  if (!workBounds) return null;

  const workAreaTop = workBounds.top;
  const workAreaLeft = workBounds.left;
  const workAreaWidth = workBounds.width;
  const workAreaHeight = workBounds.height;

  const cellInWorkArea = {
    left: cellRect.left - workAreaLeft,
    top: cellRect.top - workAreaTop,
    width: cellRect.width,
    height: cellRect.height,
  };

  const hintText = isMobile 
    ? translations.imageHintDragZoomMobile
    : translations.imageHintDragZoom;

  const imageDims = getImageDimensions();
  const imagePos = getImagePosition();
  
  // Flip transform
  const flipTransform = [
    t.flipH ? 'scaleX(-1)' : '',
    t.flipV ? 'scaleY(-1)' : '',
  ].filter(Boolean).join(' ') || undefined;

  const portalContent = (
    <>
      {/* Block header interactions */}
      <div 
        className="fixed inset-x-0 top-0 z-[10000] pointer-events-auto"
        style={{ height: workBounds.top }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
      
      {/* Toolbar */}
      <div 
        className="fixed z-[10001] flex flex-col gap-1 p-2 bg-white rounded-lg shadow-xl border"
        style={{
          right: isMobile ? 10 : 200,
          top: 10,
        }}
      >
        <ImageEditorToolbar
          onConfirm={onClose}
          onReplace={onReplace}
          onDelete={onDelete}
          onFlipH={onFlipH}
          onFlipV={onFlipV}
          onUndo={onUndo}
          onRedo={onRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          isMobile={isMobile}
          onZoomIn={() => onSetScale(Math.min(3, scale + 0.2))}
          onZoomOut={() => onSetScale(Math.max(1, scale - 0.2))}
        />
      </div>
      
      {/* Work area */}
      <div
        ref={dragRef}
        className="fixed cursor-grab active:cursor-grabbing z-[9998]"
        style={{
          left: workAreaLeft,
          top: workAreaTop,
          width: workAreaWidth,
          height: workAreaHeight,
          touchAction: 'none',
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleDragStart(e.clientX, e.clientY);
        }}
        onTouchStart={(e) => {
          if (isMobile) {
            const touchY = e.touches[0].clientY;
            const toolbarTop = window.innerHeight - MOBILE_TOOLBAR_HEIGHT;
            if (touchY >= toolbarTop) {
              return;
            }
          }
          e.preventDefault();
          e.stopPropagation();
          if (e.touches.length === 1) {
            handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
          }
        }}
      >
        {/* Darkened full image - clipped to work area only */}
        <div 
          className="absolute inset-0 overflow-hidden pointer-events-none"
        >
          <div
            className="absolute"
            style={{
              left: cellInWorkArea.left + imagePos.left,
              top: cellInWorkArea.top + imagePos.top,
              width: imageDims.width,
              height: imageDims.height,
            }}
          >
            <img
              src={cell.image}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                transform: flipTransform,
                filter: 'brightness(0.4)',
              }}
            />
          </div>
        </div>
        
        {/* Bright image clipped to cell (подход конкурента: ui-wrapper с overflow: hidden) */}
        <div
          className="absolute pointer-events-none"
          style={{
            left: cellInWorkArea.left,
            top: cellInWorkArea.top,
            width: cellInWorkArea.width,
            height: cellInWorkArea.height,
            overflow: 'hidden',
          }}
        >
          {/* draggableHelper - позиционируется через left/top */}
          <div
            style={{
              position: 'absolute',
              left: imagePos.left,
              top: imagePos.top,
              width: imageDims.width,
              height: imageDims.height,
            }}
          >
            <img
              src={cell.image}
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                transform: flipTransform,
              }}
            />
          </div>
          
          {/* Cell border frame */}
          <div
            className="absolute inset-0 pointer-events-none z-10"
            style={{
              border: '2px solid white',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          />
        </div>
        
        {/* Confirm button */}
        <div
          className="absolute w-12 h-12 bg-green-500 hover:bg-green-600 active:bg-green-700 rounded-full shadow-lg flex items-center justify-center cursor-pointer hover:scale-110 transition-transform z-20 pointer-events-auto touch-manipulation"
          style={{ 
            left: cellInWorkArea.left + cellInWorkArea.width - 24,
            top: cellInWorkArea.top - 24,
          }}
          onClick={(e) => { e.stopPropagation(); onClose(); }}
          onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Check className="w-7 h-7 text-white" />
        </div>
      </div>
      
      {/* Hint */}
      {showHint && (
        <div 
          className="fixed pointer-events-none z-[10000]"
          style={{
            left: cellRect.left + cellRect.width / 2,
            top: cellRect.top + cellRect.height + 60,
            transform: 'translateX(-50%)',
          }}
        >
          <div className="flex items-center gap-2 px-4 py-2 bg-white text-gray-800 rounded-lg shadow-lg whitespace-nowrap animate-fade-in">
            <Move className="w-4 h-4" />
            <span className="text-sm font-medium">{hintText}</span>
            <ZoomIn className="w-4 h-4" />
          </div>
        </div>
      )}
      
      {/* Mobile toolbar - positioned below canvas */}
      {isMobile && (
        <div 
          className="fixed z-[10010] flex items-center justify-center gap-1 bg-card/95 backdrop-blur-sm rounded-lg shadow-lg p-2"
          style={{ 
            touchAction: 'auto', 
            pointerEvents: 'auto',
            left: '50%',
            transform: 'translateX(-50%)',
            top: canvasBottom + 8,
          }}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-accent touch-manipulation"
            onPointerUp={(e) => { e.stopPropagation(); onReplace(); }}
          >
            <ImageIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-accent touch-manipulation disabled:opacity-50"
            disabled={!canUndo}
            onPointerUp={(e) => { e.stopPropagation(); onUndo(); }}
          >
            <Undo2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-accent touch-manipulation disabled:opacity-50"
            disabled={!canRedo}
            onPointerUp={(e) => { e.stopPropagation(); onRedo(); }}
          >
            <Redo2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-accent touch-manipulation"
            onPointerUp={(e) => { e.stopPropagation(); onFlipH(); }}
          >
            <FlipHorizontal className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-accent touch-manipulation"
            onPointerUp={(e) => { e.stopPropagation(); onFlipV(); }}
          >
            <FlipVertical className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md hover:bg-accent touch-manipulation text-red-500"
            onPointerUp={(e) => { e.stopPropagation(); onDelete(); }}
          >
            <Trash2 className="w-5 h-5" />
          </button>
          <button
            type="button"
            className="h-10 w-10 flex items-center justify-center rounded-md bg-green-500 text-white hover:bg-green-600 touch-manipulation"
            onPointerUp={(e) => { e.stopPropagation(); onClose(); }}
          >
            <Check className="w-5 h-5" />
          </button>
        </div>
      )}
    </>
  );

  return createPortal(portalContent, document.body);
};

export default ImageEditPortal;
